// Review step of the OpsAudit import (ticket 08): a flat table of the
// selected work steps where each row carries ONE workpaper decision, mirroring
// the controls Optro review step. On arrival every row's Optro files are
// fetched and written into the wizard as an explicit pick of all of them
// (`useSeedWorkStepWorkpapers`), so the cell names what the agent will read
// and the row behaves exactly like one edited by hand. The Change Workpaper
// dialog is where the set is narrowed, topped up with uploads, or emptied.
// The agent reads whatever is attached to generate accurate attributes and a
// testing table; a step with nothing attached imports as a shell to refine
// later.
//
// Two pieces of the controls page are deliberately absent, because the
// structure that feeds them does not exist on this side:
//
//   - No `MatchingWorkpaperIndicator`. Controls runs an agentic matcher over
//     the selection to resolve which workpaper belongs to which audit period.
//     An `ops_audit_item` has neither tests nor archived periods — its
//     documents hang directly off the item — so the per-row fetch here is a
//     plain listing, and "Checking Optro…" is the only pending state.
//   - No "N of M have no workpaper on file" warning and no "no workpaper only"
//     filter. Both are read-outs of that same matcher. Most work steps hold no
//     files at all (48 of 1,954 in the live tenant), so an empty row is the
//     normal case here, not a finding worth counting.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { useWorkStepOptroImport } from "../../../../contexts/workStepOptroImportContext";
import { OptroWorkpaperFile } from "../../../../models/audit/optroSync";
import {
  DEFAULT_WORKPAPER_DECISION,
  WorkStepWorkpaperDecision,
} from "../../../../models/audit/workStep";
import { useOptroWorkStepWorkpapers } from "../../../../hooks/useOptroWorkStepWorkpapers";
import { FilterCombobox } from "../../controls/optroImport/components/FilterCombobox";
import { ChangeWorkpaperDialog } from "../../controls/optroImport/components/ChangeWorkpaperDialog";
import { WorkStepWorkpaperChip } from "./components/WorkStepWorkpaperChip";
import { useOptroWorkStepImportWizard } from "./optroWorkStepImportWizardContext";
import { useSeedWorkStepWorkpapers } from "./useSeedWorkStepWorkpapers";

// What the Change Workpaper dialog shows checked for a decision, given the
// row's fetched Optro candidates: all of them under `all_optro` (plus the
// uploads), exactly the chosen files under `explicit`.
function checkedFiles(
  decision: WorkStepWorkpaperDecision,
  candidates: OptroWorkpaperFile[],
): OptroWorkpaperFile[] {
  switch (decision.mode) {
    case "all_optro":
      return [...candidates, ...decision.uploads];
    case "explicit":
      return decision.files;
  }
}

export const OptroWorkStepImportReviewPage = () => {
  const navigate = useNavigate();
  const { selected, clearSelection, workpaperDecisions, setWorkpapers } =
    useOptroWorkStepImportWizard();
  const { startImport } = useWorkStepOptroImport();
  const { isPending } = useSeedWorkStepWorkpapers(
    selected,
    workpaperDecisions,
    setWorkpapers,
  );
  const [search, setSearch] = useState("");
  // Audit names present in the selection (client-side filter; null = all).
  const [auditFilter, setAuditFilter] = useState<string | null>(null);
  const [dialogRef, setDialogRef] = useState<string | null>(null);

  // Set the moment an import is kicked off, so the empty-selection guard
  // below does not fire when `clearSelection()` empties the wizard on the way
  // out. Without it the guard's `replace` navigation wins the race against
  // the deliberate navigation to the work-steps list, and the user lands back
  // on the import start page instead of the list where the new rows appear.
  const leavingAfterImportRef = useRef(false);

  // Deep-linking into review without selections isn't meaningful.
  useEffect(() => {
    if (selected.length === 0 && !leavingAfterImportRef.current) {
      navigate("/audit/work-steps/import/optro", { replace: true });
    }
  }, [selected.length, navigate]);

  const auditOptions = useMemo(
    () =>
      Array.from(
        new Set(
          selected
            .map((w) => w.opsAuditName)
            .filter((name): name is string => name !== null),
        ),
      )
        .sort()
        .map((name) => ({ id: name, name })),
    [selected],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return selected.filter((workStep) => {
      if (
        term &&
        !workStep.name.toLowerCase().includes(term) &&
        !workStep.externalRef.toLowerCase().includes(term) &&
        !(workStep.opsAuditName ?? "").toLowerCase().includes(term)
      ) {
        return false;
      }
      if (auditFilter !== null && workStep.opsAuditName !== auditFilter) {
        return false;
      }
      return true;
    });
  }, [selected, search, auditFilter]);

  const decisionFor = (externalRef: string): WorkStepWorkpaperDecision =>
    workpaperDecisions[externalRef] ?? DEFAULT_WORKPAPER_DECISION;

  // Kicks off ONE backend import job for every selected work step (reading
  // each step's workpapers to generate attributes and a testing table;
  // nothing attached = shell import) and hands off to the global progress
  // panel — mirrors OptroImportReviewPage's onContinue for controls. A row
  // still "Checking Optro…" goes out under `DEFAULT_WORKPAPER_DECISION`, so
  // the backend resolves its files and it imports with everything anyway.
  const onImport = () => {
    startImport(
      selected.map((workStep) => ({
        workStep,
        decision: decisionFor(workStep.externalRef),
      })),
    );
    leavingAfterImportRef.current = true;
    navigate("/audit/work-steps");
    clearSelection();
  };

  const dialogWorkStep = selected.find((w) => w.externalRef === dialogRef);

  // The open row's Optro files and attachments, fetched only while the
  // dialog is open. Keyed by the numeric item datum id, which is what the
  // work-step lister route takes.
  const { workpapers: dialogCandidates, loading: dialogCandidatesLoading } =
    useOptroWorkStepWorkpapers(
      dialogWorkStep?.opsAuditItemId ?? null,
      dialogRef !== null,
    );

  return (
    <div className="h-screen flex flex-col w-full p-6 max-w-[1400px] mx-auto">
      <nav className="text-sm text-stone-500 mb-4">
        <Link to="/audit/work-steps" className="hover:underline">
          Work Steps
        </Link>
        {" › "}
        <Link to="/audit/work-steps/import/optro" className="hover:underline">
          Import from OpsAudit
        </Link>
        {" › "}
        <span className="text-stone-900">Review</span>
      </nav>

      <h1 className="text-2xl font-semibold">Review Work Steps to Import</h1>
      <p className="text-sm text-stone-500 mt-1 mb-4">
        Each row shows the work step&apos;s Optro files. Edit a row to change
        the set, upload your own, or clear it to import from work step metadata
        only.
      </p>

      <div className="shrink-0 flex items-center gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by work step or audit..."
            className="pl-9"
          />
        </div>
        <FilterCombobox
          options={auditOptions}
          value={auditFilter}
          onChange={setAuditFilter}
          allLabel="All audits"
          searchPlaceholder="Search audits..."
          emptyMessage="No audits found."
        />
      </div>

      {/* The table is the page's only scroll region; the action bar below
          stays pinned on screen regardless of row count. */}
      <div className="flex-1 min-h-0 border rounded-lg overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Work Step</TableHead>
              <TableHead>Audit</TableHead>
              <TableHead>Section</TableHead>
              <TableHead>Subsection</TableHead>
              <TableHead>Preparer</TableHead>
              <TableHead>Workpaper(s)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((workStep) => (
              <TableRow key={workStep.externalRef}>
                <TableCell className="font-medium">
                  {workStep.externalRef} - {workStep.name}
                </TableCell>
                <TableCell className="text-stone-600">
                  {workStep.opsAuditName ?? "–"}
                </TableCell>
                <TableCell className="text-stone-600">
                  {workStep.section ?? "–"}
                </TableCell>
                <TableCell className="text-stone-600">
                  {workStep.subsection ?? "–"}
                </TableCell>
                <TableCell className="text-stone-600">
                  {workStep.preparer ?? "–"}
                </TableCell>
                <TableCell>
                  <WorkStepWorkpaperChip
                    decision={decisionFor(workStep.externalRef)}
                    pending={isPending(workStep.externalRef)}
                    onEdit={() => setDialogRef(workStep.externalRef)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="shrink-0 flex items-center justify-between pt-4">
        <span className="text-sm text-stone-600">
          {selected.length} work step
          {selected.length === 1 ? "" : "s"} to import
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate("/audit/work-steps/import/optro/work-steps")
            }
          >
            Go Back
          </Button>
          <Button onClick={onImport}>Import Work Steps</Button>
        </div>
      </div>

      <ChangeWorkpaperDialog
        open={dialogRef !== null}
        onOpenChange={(open) => {
          if (!open) setDialogRef(null);
        }}
        targetKey={dialogRef}
        candidates={dialogCandidates}
        candidatesLoading={dialogCandidatesLoading}
        title="Change Workpaper"
        description="Choose the files the agent should read to configure this work step. All of the work step's Optro files are included unless you uncheck them; you can also upload your own."
        // Most work steps have no Optro file at all, so this is the state the
        // dialog is usually in. It has to read as "nothing to pick here",
        // with both ways forward spelled out — not as a failed load.
        emptyMessage="No Optro files on this work step. Upload your own below, or close to import from work step metadata only."
        current={
          dialogWorkStep
            ? checkedFiles(
                decisionFor(dialogWorkStep.externalRef),
                dialogCandidates,
              )
            : []
        }
        // Apply pins the row to exactly what is checked.
        onApply={(files) => {
          if (dialogRef) setWorkpapers(dialogRef, { mode: "explicit", files });
        }}
        // Reset re-checks every Optro file the dialog listed, keeping the
        // uploads, as an explicit pick — the same shape the seeding writes.
        // Only if the listing has not landed yet does it fall back to the
        // wire default and let the backend resolve the files.
        resetAction={{
          label: "Reset to all Optro files",
          onSelect: (chosen) => {
            if (!dialogRef) return;
            const uploads = chosen.filter((f) => f.source === "upload");
            setWorkpapers(
              dialogRef,
              dialogCandidatesLoading
                ? { mode: "all_optro", uploads }
                : {
                    mode: "explicit",
                    files: [...dialogCandidates, ...uploads],
                  },
            );
          },
        }}
      />
    </div>
  );
};
